import _                 from 'lodash'
import Immutable         from 'immutable'
import { NotFoundError } from 'lib/errors'
import AssociationProxy  from './association_proxy'

export default class EntityMap
  constructor: (@_map, @model, @associationInjector) ->

  @for: (mappable, model, injector) ->
    if mappable instanceof Immutable.OrderedMap
      map = mappable
    else
      map = new Immutable.OrderedMap(mappable.map (entity) -> [entity.pk, entity])

    new @(map, model, injector)

  for: (mappable) ->
    @constructor.for(mappable, @model, @associationInjector)

  withInjector: (injector) ->
    new @constructor(@_map, @model, injector)

  add: (entity) ->
    @_ensureEntityOfModel(entity)
    @for(@_map.set(entity.pk, entity))

  update: (entity) ->
    @_ensureEntityOfModel(entity)
    @for(@_map.set(entity.pk, entity))

  remove: (entity) ->
    @_ensureEntityOfModel(entity)
    @for(@_map.delete(entity.pk))

  first: ->
    @safeFirst() or throw new NotFoundError(@model.name)

  safeFirst: ->
    entity = @_map.minBy (val, key) -> key

    @associationInjector.inject(entity) if entity

  find: (pk) ->
    @safeFind(pk) or throw new NotFoundError(@model.name)

  findOrNullEntity: (pk) ->
    @safeFind(pk) or @nullEntity()

  safeFind: (pk) ->
    entity = @_map.get(pk)

    @associationInjector.inject(entity) if entity

  findBy: (filter) ->
    @safeFindBy(filter) or throw new NotFoundError(@model.name)

  findByOrNullEntity: (filter) ->
    @safeFindBy(filter) or @nullEntity()

  safeFindBy: (filter) ->
    filter = @_conditionsToFilter(filter) unless _.isFunction(filter)
    entity = @_map.find(filter)

    @associationInjector.inject(entity) if entity

  where: (filter) ->
    filter = @_conditionsToFilter(filter) unless _.isFunction(filter)

    # THIS INJECTS NEW INSTANCE OF AssociationProxy
    # -> this means that where has a side effect
    # -> e.g. if an entity from some collection is used as a map key, when we filter this
    # collection, the filtered entities aren't equal to the ones in the original collection,
    # and the key won't be found in the map
    # -> and not only that, in the current implementation, event the original entities have the
    # association proxy reinjected
    @for(@_map.filter((entity) => filter(@associationInjector.inject(entity))))

  orderBy: (orderer) ->
    orderer = _.property(orderer) unless _.isFunction(orderer)

    @for(@_map.sortBy(orderer))

  groupBy: (grouper) ->
    grouper = _.property(grouper) unless _.isFunction(grouper)

    @_map.groupBy(grouper).map (entities) => @for(entities)

  map: (mapper) ->
    mapper = _.property(mapper) unless _.isFunction(mapper)

    @_injectedMap().toList().map(mapper)

  flatMap: (mapper) ->
    mapper = _.property(mapper) unless _.isFunction(mapper)

    @_injectedMap().toList().flatMap(mapper)

  reduce: (reducer, initial) ->
    @_injectedMap().toList().reduce(reducer, initial)

  forEach: (fun) ->
    @_injectedMap().forEach(fun)

  # at this moment, these 3 methods rely on caller (EC) setting correct associationInjector afterwords
  union: (other) ->
    @_ensureCompatible(other, 'union')

    @constructor.for(@_map.merge(other._map), @model, @associationInjector)

  intersect: (other) ->
    @_ensureCompatible(other, 'intersect')

    @constructor.for(@toSet().intersect(other.toSet()), @model, @associationInjector)

  subtract: (other) ->
    @_ensureCompatible(other, 'subtract')

    @constructor.for(@toSet().subtract(other.toSet()), @model, @associationInjector)

  size: ->
    @_map.size

  pkeysArray: ->
    _.toArray(@_map.keys())

  pkeysList: ->
    @_map.keySeq().toList()

  toList: ->
    @_injectedMap().toList()

  toSet: ->
    @_injectedMap().toSet()

  toArray: ->
    @_injectedMap().valueSeq().toArray()

  nullEntity: ->
    @associationInjector.inject(@model.null())

  _injectedMap: ->
    @_map.map(@associationInjector.inject)

  _conditionsToFilter: (conditions) ->
    (entity) -> _.every(_.toPairs(conditions), ([key, val]) -> entity[key] == val)

  _ensureCompatible: (other, action) ->
    unless (@model == other.model and
            @associationInjector.collection == other.associationInjector.collection)
      throw Error("entity maps for #{action} must have the same model and collection")

  _ensureEntityOfModel: (entity) ->
    unless entity instanceof @model
      throw Error("can't add entity of #{entity.constructor.name} " +
                  "to entity map of #{@model.name}")
